import { useCallback, useEffect, useState } from "react";
import { Bug, RefreshCw } from "lucide-react";
import api from "../../lib/api";
import { logSwallow } from "../../lib/log";

// DEV-ONLY proximity debug panel. Renders nothing in production builds.
export default function DebugProximity() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/scan/ambient/debug");
      setData(data); setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "debug unavailable");
      logSwallow("debug.proximity", e);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (process.env.NODE_ENV === "production") return null;

  const fmt = (n) => (n === null || n === undefined ? "—" : n);
  const reasonColor = {
    out_of_radius: "text-amber-400",
    stale_presence: "text-orange-400",
    blocked: "text-[#FF6B6B]",
    no_coords: "text-zinc-500",
  };

  return (
    <div className="mt-6 rounded-2xl border border-dashed border-[#22C55E]/40 bg-[#0a0a0a]/80 p-4 font-mono-accent text-xs" data-testid="debug-proximity">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2 text-[#22C55E]"><Bug size={14} /> DEBUG · PROXIMITY (dev only)</span>
        <div className="flex items-center gap-3">
          <button onClick={load} data-testid="debug-refresh" className="flex items-center gap-1 text-zinc-400 hover:text-white"><RefreshCw size={12} /> refresh</button>
          <button onClick={() => setOpen(o => !o)} className="text-zinc-400 hover:text-white">{open ? "hide" : "show"}</button>
        </div>
      </div>

      {open && (err ? (
        <p className="mt-3 text-[#FF6B6B]" data-testid="debug-error">{err}</p>
      ) : !data ? (
        <p className="mt-3 text-zinc-500">loading…</p>
      ) : (
        <div className="mt-3 space-y-3">
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-zinc-300">
            <div>my lat: <span className="text-white" data-testid="debug-my-lat">{fmt(data.me.lat)}</span></div>
            <div>my lng: <span className="text-white" data-testid="debug-my-lng">{fmt(data.me.lng)}</span></div>
            <div>my GPS acc: <span className="text-white">{data.me.accuracy_m != null ? `±${Math.round(data.me.accuracy_m)}m` : "n/a"}</span></div>
            <div>my age: <span className="text-white">{fmt(data.me.age_seconds)}s</span></div>
            <div>radius: <span className="text-[#22C55E]" data-testid="debug-radius">{data.radius_m}m</span></div>
            <div>presence TTL: <span className="text-white">{data.presence_ttl_min}min</span></div>
            <div>scope: <span className="text-white">{data.scope}</span></div>
            <div>matched: <span className="text-white" data-testid="debug-included">{data.included_count}/{data.candidate_count}</span></div>
          </div>

          <div className="border-t border-white/5 pt-2">
            {data.candidates.length === 0 && <p className="text-zinc-500">no other users pinged recently</p>}
            {data.candidates.map((c) => (
              <div key={c.user_id} className="flex items-center justify-between py-1 border-b border-white/5 last:border-0" data-testid={`debug-candidate-${c.user_id}`}>
                <span className="text-zinc-400">{c.user_id}</span>
                <span className="text-white">{c.distance_m != null ? `${c.distance_m.toFixed(1)}m` : "no-coords"}</span>
                <span className="text-zinc-500">acc {c.accuracy_m != null ? `±${Math.round(c.accuracy_m)}m` : "n/a"}</span>
                <span className="text-zinc-500">{c.age_seconds}s</span>
                {c.included
                  ? <span className="text-[#22C55E]" data-testid={`debug-status-${c.user_id}`}>INSIDE ✓</span>
                  : <span className={reasonColor[c.reason] || "text-zinc-400"} data-testid={`debug-status-${c.user_id}`}>{c.reason}</span>}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
